import React from 'react';
import { Chip, type ChipProps } from '@mui/material';
import { categoryChipSx } from '../utils/categoryChipColor';

/** Matches Tx Review Prop column chips (size/height/label padding). */
export const PROPERTY_NICKNAME_CHIP_SX = {
  maxWidth: 160,
  height: 22,
  fontWeight: 500,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    px: 0.75,
  },
} as const;

/**
 * Same hash palette as Tx Review Category chips (`categoryChipSx`), keyed by nickname
 * so 2SA / 28LC / 87HP / 67RR stay distinct and consistent across Bookings and TX.
 */
export function propertyNicknameChipSx(nickname?: string | null) {
  const n = String(nickname || '').trim();
  if (!n) return {};
  return categoryChipSx(n);
}

export function formatPropertyNickname(
  nickname?: string | null,
  propertyId?: number | null
): string {
  const n = String(nickname || '').trim();
  if (n) return n;
  if (propertyId != null && Number.isFinite(Number(propertyId))) {
    return `Property ${propertyId}`;
  }
  return '';
}

type PropertyNicknameChipProps = {
  nickname?: string | null;
  propertyId?: number | null;
} & Omit<ChipProps, 'label'>;

/**
 * Canonical property nickname tag used across the app (Tx Review Prop style + category palette colours).
 */
const PropertyNicknameChip: React.FC<PropertyNicknameChipProps> = ({
  nickname,
  propertyId,
  sx,
  variant = 'filled',
  ...rest
}) => {
  const label = formatPropertyNickname(nickname, propertyId);
  if (!label) return null;
  const colorSx = propertyNicknameChipSx(label);
  return (
    <Chip
      size="small"
      variant={variant}
      label={label}
      title={label}
      sx={{ ...PROPERTY_NICKNAME_CHIP_SX, ...colorSx, ...(sx as object) }}
      {...rest}
    />
  );
};

export default PropertyNicknameChip;
